import { Directive, Injector, Input, OnInit } from '@angular/core';
import { AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import { IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { untilDestroyed } from '@ngneat/until-destroy';
import _, { cloneDeep } from 'lodash';
import { combineLatest, find } from 'rxjs';
import { TAXONAMY } from '../../../../aumico-lib-annualreport-fe/src/lib/constants/autofill-options';
import { AumicoCoreProvider } from '../../../../aumico-lib-core-fe/src/lib';
import { ArFeatureBaseModel } from './arFeatureBase.model';
import { ArFeatureBaseInboundService } from './arFeatureBaseInbound.service';
import { ArFeatureBaseStateServiceAdapter } from './arFeatureBaseState.service';
import { ArFeatureDataState } from './arFeatureDataState.enum';
import { ArFeatureRenderingMode } from './arFeatureRenderingMode.enum';

@Directive()
export abstract class ArFeatureBaseAdapter<Model extends ArFeatureBaseModel>
    implements ArFeatureBaseInboundService<Model>, OnInit
{
    public ArFeatureRenderingMode = ArFeatureRenderingMode;
    protected readonly annualReportProvider: AnnualReportProvider;
    protected readonly coreProvider: AumicoCoreProvider;
    protected _renderingMode: ArFeatureRenderingMode = ArFeatureRenderingMode.UNDEFINED;
    protected _dataState: ArFeatureDataState = ArFeatureDataState.UNDEFINED;
    get dataState(): ArFeatureDataState {
        return this._dataState;
    }
    set dataState(state: ArFeatureDataState) {
        this._dataState = state;
        this.stateService.updateState(state);
    }

    protected _domainModel: Model;
    protected _domainModelUsedForResetting: Model;

    get domainModel(): Model {
        return this._domainModel;
    }
    set domainModel(domainModel: Model) {
        this._domainModel = domainModel;
        this.stateService.updateDomainModel(domainModel);
    }

    @Input()
    translationLocale: string;

    @Input()
    public get renderingMode(): ArFeatureRenderingMode {
        return this._renderingMode;
    }
    public set renderingMode(mode: ArFeatureRenderingMode) {
        this._renderingMode = mode;
    }

    constructor(protected injector: Injector, protected stateService: ArFeatureBaseStateServiceAdapter<Model>) {
        this.annualReportProvider = injector.get(AnnualReportProvider);
        this.coreProvider = injector.get(AumicoCoreProvider);
    }

    ngOnInit(): void {
        this.initializeSubscriptions();
    }

    initializeSubscriptions(): void {
        this.dataState = ArFeatureDataState.LOADING;

        const previousYearAnnualReport$ = this.annualReportProvider.getAnnualReportForBalanceAutoFill$();
        const previousYearFinStatements$ = this.annualReportProvider.getBalanceAutofillFinStatements$();
        const selectedYearAutofillAnnualReport$ =
            this.annualReportProvider.getAnnualReportForUploadAndMappingAutoFill$();
        const selectedYearAutofillFinStatements$ =
            this.annualReportProvider.getSelectedYearForUploadAndMappingAutofillFinStatements$();
        const activeFinStatements$ = this.annualReportProvider.getActiveFinStatements$();

        const activeAnnualReport$ = this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(find((activeAnnual: IAnnualReport) => activeAnnual != null));

        combineLatest([
            previousYearAnnualReport$,
            activeAnnualReport$,
            selectedYearAutofillAnnualReport$,
            previousYearFinStatements$,
            activeFinStatements$,
            selectedYearAutofillFinStatements$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(
                ([
                    previousYearAnnualReport,
                    activeAnnualReport,
                    selectedYearAutofillAnnualReport,
                    previousYearFinStatements,
                    activeFinStatements,
                    selectedYearAutofillFinStatements,
                ]) => {
                    const activeAnnualReportCloned: IAnnualReport = cloneDeep<IAnnualReport>(activeAnnualReport);
                    const activeFinStatementsCloned: IFinStatements[] =
                        cloneDeep<IFinStatements[]>(activeFinStatements);
                    if (
                        this.dataState < ArFeatureDataState.INITILIZED_WITH_CURRENT_YEAR_DATA &&
                        !this.tryTakeSavedData(activeAnnualReportCloned)
                    ) {
                        const uploadAndMappingAutoFillAnnualReportId =
                            activeAnnualReportCloned?.templateReportAutofillOptions?.[TAXONAMY];
                        if (
                            !uploadAndMappingAutoFillAnnualReportId ||
                            !this.tryAutofillFromOtherAnnualReport(
                                selectedYearAutofillAnnualReport,
                                selectedYearAutofillFinStatements,
                                activeAnnualReportCloned,
                                activeFinStatementsCloned,
                                false,
                            )
                        ) {
                            if (
                                !activeAnnualReportCloned.generalInformation
                                    .isAutoFillFromPreviousAnnualReportEnabled ||
                                !this.tryAutofillFromOtherAnnualReport(
                                    previousYearAnnualReport,
                                    previousYearFinStatements,
                                    activeAnnualReportCloned,
                                    activeFinStatementsCloned,
                                    false,
                                )
                            ) {
                                this.tryAutofillFromCurrentYear(
                                    activeAnnualReportCloned,
                                    activeFinStatementsCloned,
                                    false,
                                );
                            }
                        }
                    }
                },
            );
    }

    renderFeatureForm() {
        this.renderingMode = ArFeatureRenderingMode.FORM;
    }

    renderFeatureData() {
        this.renderingMode = ArFeatureRenderingMode.PRINT_PREVIEW;
    }

    tryAutofillFromOtherAnnualReport(
        annualReportForAutofill: IAnnualReport,
        finStatementsForAutofill: IFinStatements[],
        currentYearAnnualReport: IAnnualReport,
        currentYearFinStatements: IFinStatements[],
        overwrite: boolean,
    ) {
        // Guard to not overwrite data which are taken from saved annual report
        if (currentYearFinStatements.length === 0) return this.returnError();
        if (currentYearFinStatements[0].templateAndAccountsMapping.template === null) return this.returnError();
        let result = false;
        if (this.dataState > ArFeatureDataState.INITILIZED_FROM_PREVIOUS_YEAR && !overwrite) return result;
        try {
            result = this.handleAutofillFromOtherAnnualReport(
                annualReportForAutofill,
                finStatementsForAutofill,
                currentYearAnnualReport,
                currentYearFinStatements,
                overwrite,
            );
        } catch (error) {
            this.domainModel = undefined;
            this.dataState = ArFeatureDataState.ERROR;
            return result;
        }
        if (result) {
            this.dataState = ArFeatureDataState.INITILIZED_FROM_PREVIOUS_YEAR;
            this._domainModelUsedForResetting = _.cloneDeep(this.domainModel);
        }
        return result;
    }

    returnError() {
        this.dataState = ArFeatureDataState.ERROR;
        return false;
    }

    tryAutofillFromCurrentYear(original: IAnnualReport, finStatements: IFinStatements[], overwrite: boolean) {
        // Guard to not overwrite data which are taken from previous year
        if (this.dataState === ArFeatureDataState.ERROR) return;
        if (this.dataState > ArFeatureDataState.INITILIZED_WITH_CURRENT_YEAR_DATA && !overwrite) return;
        try {
            this.handleAutofillFromCurrentYear(original, finStatements, overwrite);
        } catch (error) {
            this.domainModel = undefined;
            this.dataState = ArFeatureDataState.ERROR;
            return;
        }
        this.dataState = ArFeatureDataState.INITILIZED_WITH_CURRENT_YEAR_DATA;
        this._domainModelUsedForResetting = _.cloneDeep(this.domainModel);
    }

    tryTakeSavedData(input: IAnnualReport): boolean {
        const result = this.takeSavedData(input);
        if (result) {
            this.dataState = ArFeatureDataState.INITILIZED_FROM_SAVED_DATA;
            this._domainModelUsedForResetting = _.cloneDeep(this.domainModel);
        }
        return result;
    }

    public resetDataState() {
        this.domainModel = _.cloneDeep(this._domainModelUsedForResetting);
    }

    //Methods to be implemented in Feature Adapter
    abstract handleAutofillFromOtherAnnualReport(
        previouseYear: IAnnualReport,
        previouseYearFinStatements: IFinStatements[],
        currentYear: IAnnualReport,
        currentYearFinStatements: IFinStatements[],
        overwrite: boolean,
    ): boolean;
    abstract handleAutofillFromCurrentYear(
        original: IAnnualReport,
        originalFinStatements: IFinStatements[],
        overwrite: boolean,
    );
    abstract takeSavedData(original: IAnnualReport): boolean;
}
